
import Vue from 'vue';

// Services
import UserConsentContext, { CONSENT_OPTIONS } from '~/services/UserConsentContext';
import UserInfoService from '~/services/UserInfoService';
import VFairService, {
    VFair,
    VFairPavilion,
    VFairProfileRequirementLevel,
    VFairUser,
} from '~/services/VFairService';
import {
    CandidateProfileScorer,
    CandidateProfileStatus,
    ProfileRequirementType,
} from '~/utils/candidate/CandidateProfileScorer';
import DialogService from '~/services/DialogService';
import i18n from '~/localization/i18n';
import { RestXReactiveData } from '~/utils/types';
import { AxiosError } from 'axios';
import { StudyProgram } from '~/services/CSPService';

// Components
import MinimalProfileModal from '~/components/profile/modals/MinimalProfileModal.vue';
import CKEditorContent from '~/components/builders/CKEditorContent.vue';
import Paper from '~/components/builders/Paper.vue';
import Pavilions from '../components/Pavilions.vue';
import MainLayout from '~/components/layouts/mainlayout/MainLayout.vue';
import RegisterSuccessModal from '../components/RegisterSuccessModal.vue';
import VFairHeader, { HeaderButtonType } from '../components/VFairHeader.vue';

// Utils
import addDays from 'date-fns/addDays';
import isAfter from 'date-fns/isAfter';
import isWithinInterval from 'date-fns/isWithinInterval';
import URLS from '~/URLS';
import AxiosUtils from '~/utils/axios';
import { buildIDParam, uuidFromURLParam } from '~/utils/urlUtils';
import { VFairAccess, VFairUtils, calculateVFairAccess } from '../utils';
import isBefore from 'date-fns/isBefore';

// Assets
import MasterClassImage from '../../../assets/img/vcf/masterclassSucceed.png';

// Constants
const externalEvents: Record<string, { registrationLink: string }> = {
    '90ceeaee-0b1e-4abf-86a5-9e70713f74c3': {
        registrationLink: 'https://careers.se.com/event-10986/talentcommunity/form',
    },
    '83120a69-2827-40c4-a8c7-42cfbbca4e47': {
        registrationLink:
            'https://student-se.icims.com/jobs/71041/schneider-global-student-experience-2024/job?iisc=CRM&iis=direct_email&iisn=249870&mobile=false&width=1920&height=500&bga=true&needsRedirect=false&jan1offset=60&jun1offset=120',
    },
};

export default Vue.extend({
    name: 'VFairDetails',
    components: {
        MainLayout,
        VFairHeader,
        Pavilions,
        CKEditorContent,
        Paper,
        MinimalProfileModal,
        RegisterSuccessModal,
    },
    data() {
        return {
            isLoadingUser: false,
            masterClassImage: MasterClassImage,
            hasAdminAccess: false,
            showProfileModal: false,
            isRegistering: false,
            registrationCompleted: false,

            pavilionId: null as string | null,
            pavilions: [] as VFairPavilion[],
            pavilionsLoading: false,

            activityUpdater: null as number | null,
        };
    },
    computed: {
        interfaces(): any {
            return (this as any).$interfaces;
        },
        vfair(): Partial<RestXReactiveData<VFair>> {
            if (this.vFairId) {
                return this.interfaces.vfair.id(this.vFairId).get({
                    reactive: true,
                });
            } else {
                return { loading: true };
            }
        },
        vFairUser: {
            get(): VFairUser | undefined {
                return (this as any).$interfaces.vfairModule.getVFairUser();
            },
            set(newValue: VFairUser) {
                (this as any).$interfaces.vfairModule.setVFairUser(newValue);
            },
        },
        vFairProfileRequirementType():
            | ProfileRequirementType.VFAIR_PROFILE
            | ProfileRequirementType.VFAIR_PROFILE_MINIMAL {
            return this.vfair.data?.profileRequirementLevel === VFairProfileRequirementLevel.Minimum
                ? ProfileRequirementType.VFAIR_PROFILE_MINIMAL
                : ProfileRequirementType.VFAIR_PROFILE;
        },

        hasAccess(): boolean {
            if (!this.isAuthenticated || !this.data) return false;

            if (this.data.personalInvites) {
                if (this.hasAdminAccess) return true;
                if (!this.vFairUser) return false;
                return this.vFairUser.invited;
            }

            return true;
        },
        isAuthenticated(): boolean {
            return this.interfaces.logInModule.isLoggedIn();
        },
        isOngoing(): boolean {
            const { start, end } = this.data;
            if (typeof start === 'undefined') return false;
            if (!end || isNaN(start.getTime()) || isNaN(end.getTime())) return true;
            if (isBefore(end, start)) return false;

            return isWithinInterval(new Date(), { start, end });
        },
        isInvited(): boolean {
            if (this.hasAdminAccess) return true;
            if (!this.vFairUser) return false;
            return this.vFairUser.invited;
        },
        isInterested(): boolean {
            if (this.vFairUser) return this.vFairUser.interested;
            else return false;
        },
        hackEnabled(): boolean {
            return this.interfaces.vfairModule.getHackEnabled();
        },

        data(): Partial<VFair> {
            if (Array.isArray(this.vfair.data)) return {};

            const vfair: Partial<VFair> & { start: Date; end: Date } = this.vfair.data || {
                start: new Date(),
                end: new Date(),
            };
            return {
                ...vfair,
                start: new Date(vfair.start),
                end: new Date(vfair.end),
            };
        },

        showPavilions(): boolean {
            return (
                !!this.pavilions.length &&
                !!this.vFairId &&
                this.hasAccess &&
                this.isInterested &&
                (this.isOngoing || this.hackEnabled)
            );
        },
        registerButtonType(): HeaderButtonType {
            // No button: If loading, no data or user not invited (with personal invite type event)
            if (
                this.loading() ||
                !this.data ||
                (this.data.personalInvites && this.vFairUser && this.vFairUser.invited == false)
            )
                return null;

            const now = new Date();
            const { start, end } = this.data;

            // Enter-button: If start/end is not valid date
            if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return 'enter';
            // Enter-button: If user is registered, and event has begun
            if (this.isInterested && isWithinInterval(now, { start, end })) return 'enter';
            // No button: If event is over
            if (isAfter(now, end)) return null;

            // HACK
            // Make sure external events always have register button
            if (this.vFairId && !!externalEvents[this.vFairId]) {
                return 'register';
            }
            // HACK END

            // Register-button: If event has not begun, or user is not registered
            return this.isInterested ? 'calendar' : 'register';
        },
        hideRegisterButton(): boolean {
            return this.showPavilions && this.registerButtonType === 'enter';
        },
        vFairId(): string | null {
            return uuidFromURLParam(this.$route.params.id);
        },
        isExternalEvent(): boolean {
            return this.vFairId && !!externalEvents[this.vFairId];
        },
    },
    methods: {
        checkAdminAccess(): void {
            const vFairId = this.vFairId || '';
            if (UserInfoService.isHigheredAdmin()) {
                this.hasAdminAccess = true;
                return;
            }

            const adminIds = UserInfoService.getOrgAdminIds();
            if (adminIds.length > 0) {
                VFairService.isAnyOrganizationActiveOnVfair(vFairId, adminIds).then(active => {
                    if (active) {
                        this.hasAdminAccess = true;
                    }
                });
                VFairService.isAnySchoolActiveOnVfair(vFairId, adminIds).then(active => {
                    if (active) {
                        this.hasAdminAccess = true;
                    }
                });
            } else {
                this.hasAdminAccess = false;
            }
        },
        loading(): boolean {
            return !!this.vfair.loading;
        },

        goToVFair() {
            if (!this.vfair.data || !this.vfair.data.id) return;

            const pavilion = !!this.pavilionId ? `?pavilion_id=${this.pavilionId}` : '';
            this.pavilionId = null;

            this.$router.push(
                `${URLS.vfair}/${buildIDParam(this.vfair.data.id, this.vfair.data.title)}${
                    URLS.vfairBooths
                }${pavilion}`
            );
        },

        async handleInterestedClick() {
            if (!this.isAuthenticated) {
                this.interfaces.logInModule.requireLogIn(this.$route.path);
                return;
            }

            const scorer: CandidateProfileScorer | null = await this.$store.getters[
                'profileModule/awaitCandidateProfileScorer'
            ]();
            if (!scorer) {
                console.warn('was not able to get CandidateProfileScorer');
                return;
            }

            const score = scorer.getVFairProfileScore(
                this.data.profileRequirementLevel === VFairProfileRequirementLevel.Minimum
            );
            if (score.status === CandidateProfileStatus.INCOMPLETE) {
                this.showProfileModal = true;
            }

            if (
                score.status === CandidateProfileStatus.COMPLETE &&
                this.registerButtonType === 'register'
            ) {
                // HACK
                const isExternalEvent = this.vFairId && !!externalEvents[this.vFairId];

                // HACK END

                const closeAction = ({ res }: { res: (s: string) => void }) => res('close');
                const executeAndCloseAction = ({ res }: { res: (s: string) => void }) => {
                    res('close');
                    this.handleRegister(false);
                };
                DialogService.openDialog({
                    title: 'Event registration',
                    contentHTML: isExternalEvent
                        ? i18n.t('vfair.profile.proceedRegisterText')
                        : `Please confirm your registration for the <b>${this.vfair.data?.title}</b>.`,
                    actions: [
                        {
                            id: 'vfair-register-confirm',
                            title: isExternalEvent
                                ? i18n.t('vfair.profile.proceedRegister')
                                : i18n.t('vfair.profile.confirmRegister'),
                            variant: 'primary',
                            action: executeAndCloseAction,
                        },
                        {
                            id: 'vfair-register-cancel',
                            title: i18n.t('general.cancel'),
                            variant: 'text-secondary',
                            action: closeAction,
                        },
                    ],
                });
                return;
            }
            if (
                score.status === CandidateProfileStatus.COMPLETE &&
                this.registerButtonType === 'enter'
            ) {
                this.goToVFair();
                return;
            }
        },
        handleRegister(showCompleted = false) {
            if (this.registerButtonType === 'enter') {
                this.isLoadingUser = false;
                this.isRegistering = false;
                this.registrationCompleted = true;
                this.showProfileModal = false;

                this.goToVFair();
                return;
            }

            this.showProfileModal = false;
            this.isLoadingUser = true;
            this.isRegistering = true;

            this.interfaces.vfairuser.user.me
                .vfair_id(this.vFairId)
                .post({ body: { interested: true } })
                .then(() => {
                    this.interfaces.vfairuser.user.me
                        .vfair_id(this.vFairId)
                        .get({ forceUpdate: true })
                        .then((user: any) => {
                            this.vFairUser = user.data;
                            this.isLoadingUser = false;
                            this.isRegistering = false;
                            // this.registrationCompleted = true;

                            // HACK
                            if (this.vFairId && !!externalEvents[this.vFairId]) {
                                window.open(
                                    externalEvents[this.vFairId].registrationLink,
                                    '_blank'
                                );
                                return;
                            } else {
                                this.registrationCompleted = true;
                            }
                            // HACK END

                            if (showCompleted) this.showProfileModal = true;
                        });
                });
        },
        handleOpenPavilion(pavilionId: string) {
            if (!this.data.id) return;
            this.pavilionId = pavilionId;
            this.handleInterestedClick();
        },

        fetchPavilions() {
            if (!this.vFairId) return;

            this.pavilionsLoading = true;
            VFairService.getPavilions(this.vFairId)
                .then(res => (this.pavilions = res.data))
                .finally(() => (this.pavilionsLoading = false));
        },
        fetchVFairUserData() {
            this.isLoadingUser = true;
            this.interfaces.vfairuser.user.me
                .vfair_id(this.vFairId)
                .get({
                    forceUpdate: true,
                })
                .then((data: any) => {
                    if (data && data.data && data.data.id) {
                        this.vFairUser = data.data;
                    }
                })
                .catch(console.error)
                .finally(() => (this.isLoadingUser = false));
        },
        async initWebinarMessages() {
            const vFairId = this.vFairId;
            if (!vFairId) return;

            const from = new Date();
            const to = addDays(from, 1);

            const result = await VFairService.getWebinars(vFairId, {
                shallow: true,
                from,
                to,
            });
            if (!result || result.status !== 200) return;

            const { data: webinars } = result;
            if (!webinars.length) return;

            const withVFairId = webinars.map(wb => ({ ...wb, vFairId }));

            const service = this.interfaces.webinarMessageModule.getService();
            if (!service) return;
            service.addWebinars(withVFairId);
        },
        updateUserActivity() {
            if (this.hasAccess && this.isOngoing) {
                const vFairId = this.vFairId;
                return VFairService.updateUserActivity(vFairId || '');
            }
        },
        checkAccessToVFair(vfair: VFair) {
            return calculateVFairAccess(vfair, { awaitCSPs: true }).then(result => {
                if (result.access !== VFairAccess.Access) {
                    this.$router.replace(URLS.vfair);
                }
            });
        },
    },
    mounted() {
        this.$store.getters['profileModule/awaitMostRelevantSchool']().then(
            (s: StudyProgram['school'] | null) => {
                this.interfaces.communication.chat.user.me.get({
                    analytics_consent: UserConsentContext.getOption(CONSENT_OPTIONS.ANALYTICS),
                    school_id: s ? s.id : undefined,
                    school_name: s ? s.name : undefined,
                });
            }
        );

        this.checkAdminAccess();
        this.interfaces.vfairModule.setPavilionId(null);
        if (this.isAuthenticated) this.fetchVFairUserData();

        this.fetchPavilions();
        this.initWebinarMessages();

        this.activityUpdater = setInterval(this.updateUserActivity, 5000);
    },
    beforeDestroy() {
        if (this.activityUpdater) clearInterval(this.activityUpdater);
    },
    watch: {
        vfair: {
            handler(vfair: RestXReactiveData<VFair, AxiosError>) {
                if (vfair.loading) {
                    return;
                }

                if (AxiosUtils.isErrorNotFound(vfair.error)) {
                    this.$router.replace(URLS.vfair);
                } else if (!vfair.error) {
                    this.checkAccessToVFair(vfair.data);
                }
            },
            immediate: true,
        },
        registerButtonType: {
            handler(type: HeaderButtonType) {
                // Emit event
                if (this.data) {
                    window.dispatchEvent(
                        new CustomEvent(VFairUtils.vfairModeEventName, {
                            detail: VFairUtils.getVFairModeEventPayload(type === 'enter'),
                        })
                    );
                }
            },
            immediate: true,
        },
    },
});
